import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { z } from 'zod';
import { Button, Card, CardBody, CardTitle, Field } from '../../ui';
import { callFunction } from '../../shared/functions';
import { DemoInviteLink, useRevealedLink } from './DemoInviteLink';

/**
 * Invite a client by email.
 *
 * The browser sends a name and an address and nothing else. Which instance the
 * new client belongs to is decided by `invite-client` from the coach's own
 * profile row — if this form could name a `coach_id`, it could name someone
 * else's (design.md F4).
 *
 * The invitation link is normally never returned here: it exists in one place,
 * the mailbox it was sent to, which is what makes "single-use and revocable"
 * mean something. There is one fenced exception, and only one — an address
 * whose domain ends in `.test`, outside production, behind an explicit flag,
 * comes back with its link so the coach can walk the client half of the flow
 * herself on the deployed site (change `deployed-invite-unblock`). `.test` is
 * reserved by RFC 2606, so such an address reaches no mailbox and belongs to
 * nobody. An address that can receive mail never has its link shown, here or
 * anywhere; see `DemoInviteLink`, which checks the address a second time.
 */

const schema = z.object({
  displayName: z.string().trim().min(1, 'Enter their name.').max(80, 'That is too long.'),
  email: z.email('That is not an address.'),
});

type Values = z.infer<typeof schema>;

export function InviteClientForm() {
  const queryClient = useQueryClient();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const { revealed, reveal, clear } = useRevealedLink();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { displayName: '', email: '' },
  });

  const invite = useMutation({
    mutationFn: (values: Values) =>
      callFunction<{ ok: true; clientId: string; link?: string }>('invite-client', {
        action: 'invite',
        displayName: values.displayName,
        email: values.email,
      }),
    onSuccess: async (data, values) => {
      setSentTo(values.email);
      reveal(values.email, data.link);
      reset();
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
  });

  const onSubmit = handleSubmit((values) => {
    setSentTo(null);
    clear();
    invite.mutate(values);
  });

  return (
    <Card>
      <CardTitle>Invite a client</CardTitle>
      <CardBody>
        They get one link, good once, that expires in fourteen days. You can resend or revoke it
        until they use it.
      </CardBody>

      <form className="mt-5 flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)}>
        <Field
          label="Name"
          autoComplete="off"
          error={errors.displayName?.message}
          {...register('displayName')}
        />
        <Field
          label="Email"
          type="email"
          autoComplete="off"
          error={errors.email?.message}
          {...register('email')}
        />

        {invite.isError ? (
          <p role="alert" className="text-danger text-sm">
            {invite.error.message}
          </p>
        ) : null}

        {sentTo === null ? null : (
          <p role="status" className="text-primary text-sm">
            Invitation sent to {sentTo}.
          </p>
        )}

        <DemoInviteLink revealed={revealed} />

        <div>
          <Button type="submit" disabled={invite.isPending}>
            {invite.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
